// Logística por jornada (multievento). Con el selector de jornadas de la
// pestaña Logística (patrón de Material):
// - ResumenJornadas ("Todas", la vista por defecto): una fila por jornada con
//   fecha, horas, lugar y transporte; al pulsar una fila se abre esa jornada.
// - LugarJornada (nivel 1 con una jornada elegida): lugar, dirección, acceso,
//   ruta desde la nave, coste interno del viaje, zona y Madrid Central de la
//   jornada. Sin dirección propia, la jornada hereda la dirección principal.
// - HorarioJornadaCampos (nivel 2): fecha, hora de inicio, de fin y de llegada.
// Los cambios se guardan en la jornada con el PUT de la jornada (solo los
// campos que cambian; `borrar` para vaciarlos), por `onGuardarJornada` de la
// ficha. La zona y Madrid Central de cada jornada los calcula el backend
// (estadoReglas.transporte.jornadas).
import React, { useEffect, useState } from 'react';
import { placesAPI } from '../../../api/api';
import DireccionAutocomplete from '../../DireccionAutocomplete';
import {
  costeRutaInterno, direccionCorta, eurosCorto, formatEuros, numeroEs, textoRutaInterna, urlMapa,
} from '../../../utils/logistica2';
import { formatoMinutos } from '../../../utils/quienVaEvento';
import CosteViaje from './CosteViaje';
import useEsMovil from '../../../hooks/useEsMovil';
import { botonSecundario, claseSubtitulo, enlace, enlacePequeno } from './estilos';

// Campos de lugar y ruta de la jornada (se vacían para volver a la dirección principal)
export const CAMPOS_LUGAR_JORNADA = [
  'lugar_evento', 'direccion_exacta', 'coordenadas', 'place_id', 'municipio',
  'distancia_km', 'tiempo_viaje_min', 'coste_transporte',
];
const CAMPOS_RUTA = ['distancia_km', 'tiempo_viaje_min', 'coste_transporte'];

const fechaCorta = (iso) => (iso ? `${String(iso).slice(8, 10)}/${String(iso).slice(5, 7)}/${String(iso).slice(0, 4)}` : '');
const DIAS = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
const diaSemana = (iso) => {
  if (!iso) return '';
  const f = new Date(`${String(iso).slice(0, 10)}T12:00:00`);
  return Number.isNaN(f.getTime()) ? '' : DIAS[f.getDay()];
};
const claseCampo = 'px-2 py-1.5 min-h-[44px] md:min-h-0 text-base md:text-sm border border-[#CBD3CF] rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary';

export const etiquetaJornada = (j, i) => {
  const f = String(j?.fecha || '').slice(0, 10);
  return `Jornada ${i + 1}${f.length === 10 ? ` · ${f.slice(8, 10)}/${f.slice(5, 7)}` : ''}`;
};

// Texto de las horas de una jornada: "09:00 a 14:00 (llegada 08:15)"
export const textoHorasJornada = (j) => {
  if (!j) return '';
  const base = j.hora_inicio && j.hora_fin ? `${j.hora_inicio} a ${j.hora_fin}` : (j.hora_inicio || 'Sin hora');
  return j.hora_llegada_estimada ? `${base} (llegada ${j.hora_llegada_estimada})` : base;
};

// El presupuesto visto como la jornada: su lugar y su ruta (o los heredados)
// ¿Hereda la jornada la dirección principal? Manda lo que dice el backend
// (una jornada con la misma dirección que la principal también hereda)
export const jornadaHeredaDireccion = (jornada, tJ) => (
  tJ && typeof tJ.hereda_direccion === 'boolean'
    ? tJ.hereda_direccion
    : !String(jornada?.direccion_exacta || jornada?.lugar_evento || '').trim()
);

export const TEXTO_RUTA_HEREDADA = 'Ruta heredada de la dirección principal';

export const presupuestoDeJornada = (p, jornada, tJ, modo = null) => {
  if (!jornada) return p;
  const propio = !jornadaHeredaDireccion(jornada, tJ);
  const lugar = propio
    ? { lugar_evento: jornada.lugar_evento, direccion_exacta: jornada.direccion_exacta, place_id: jornada.place_id, coordenadas: jornada.coordenadas }
    : { lugar_evento: p.lugar_evento, direccion_exacta: p.direccion_exacta, place_id: p.place_id, coordenadas: p.coordenadas };
  const km = tJ ? tJ.distancia_km : (propio ? jornada.distancia_km : (jornada.distancia_km || p.distancia_km));
  const minutos = tJ ? tJ.tiempo_viaje_min : (propio ? jornada.tiempo_viaje_min : (jornada.tiempo_viaje_min || p.tiempo_viaje_min));
  let coste = jornada.coste_transporte;
  if (coste === null || coste === undefined) coste = tJ ? (tJ.coste_transporte ?? tJ.coste_total) : (propio ? null : p.coste_transporte);
  return {
    ...p,
    ...lugar,
    distancia_km: km,
    tiempo_viaje_min: minutos,
    coste_transporte: coste,
    hora_inicio: jornada.hora_inicio || p.hora_inicio,
    hora_fin: jornada.hora_fin || p.hora_fin,
    hora_llegada_estimada: jornada.hora_llegada_estimada || p.hora_llegada_estimada,
    fecha_evento: jornada.fecha || p.fecha_evento,
    quien_va_evento: modo ? { ...(p.quien_va_evento || {}), modo } : p.quien_va_evento,
  };
};

const textoTransporteJornada = (tJ) => {
  if (!tJ) return 'Calculando...';
  const partes = [];
  if (Number(tJ.distancia_km) > 0) {
    partes.push(`${numeroEs(tJ.distancia_km)} km · ${formatoMinutos(tJ.tiempo_viaje_min)} por trayecto`);
    if (tJ.ruta_heredada || (tJ.hereda_direccion && tJ.hereda_ruta)) partes.push(TEXTO_RUTA_HEREDADA);
  } else if (tJ.hereda_direccion) {
    partes.push(TEXTO_RUTA_HEREDADA);
  } else {
    partes.push('Falta la ruta');
  }
  if (tJ.zona_gratuita) partes.push(`${tJ.municipio || 'Zona'}: zona gratuita`);
  else if (tJ.zona) partes.push(`zona ${tJ.zona} (${eurosCorto(tJ.importe)})`);
  return partes.join(' · ');
};

// ─────────────────────────────────────────────────────────────────────────────
// "Todas": resumen de las jornadas
// ─────────────────────────────────────────────────────────────────────────────

export const ResumenJornadas = ({ presupuesto, jornadasTransporte = [], onElegir }) => {
  // Móvil (< 768 px): una tarjeta por jornada en vez de la tabla
  const esMovil = useEsMovil();
  const p = presupuesto || {};
  const jornadas = p.jornadas || [];
  const lineaRaiz = direccionCorta(p.direccion_exacta || p.lugar_evento).linea || p.lugar_evento || '';
  if (esMovil) {
    return (
      <div className="flex flex-col gap-2" data-testid="l2-resumen-jornadas">
        {jornadas.map((j, i) => {
          const tJ = jornadasTransporte.find(x => x.id === j.id) || null;
          const hereda = jornadaHeredaDireccion(j, tJ);
          const linea = hereda
            ? lineaRaiz
            : (direccionCorta(j.direccion_exacta || j.lugar_evento).linea || j.lugar_evento || '');
          return (
            <div key={j.id} className="rounded-xl border border-[#E1E6E3] bg-white px-3 py-2.5 flex flex-col gap-1 text-sm"
              data-testid={`l2-resumen-jornada-${j.id}`}>
              <button type="button" onClick={() => onElegir && onElegir(j.id)}
                className="min-h-[44px] -mx-1 px-1 flex items-center justify-between gap-3 text-left rounded-lg active:bg-[#F7F9F8]"
                data-testid={`l2-resumen-abrir-${j.id}`}>
                <span className="font-semibold text-[#11704F]">Jornada {i + 1}</span>
                <span className="text-[13px] text-[#33443C] tabular-nums text-right">
                  {fechaCorta(j.fecha) || 'Sin fecha'}{diaSemana(j.fecha) ? ` · ${diaSemana(j.fecha)}` : ''}
                </span>
              </button>
              <span className="text-[13px] tabular-nums text-[#33443C]">{textoHorasJornada(j)}</span>
              <span className="text-[13px] text-[#16221D] break-words">
                {linea || <span className="text-[#92400E]">Falta la dirección</span>}
                {hereda && linea && <span className="block text-xs text-[#5B6B64]">Hereda la dirección principal</span>}
              </span>
              {tJ?.madrid_central_dentro === true && (
                <span className="self-start inline-flex items-center rounded-full border border-[#F2C4BF] bg-[#FDECEA] px-2 py-0.5 text-xs font-semibold text-[#8A1C12]">
                  Dentro de Madrid Central
                </span>
              )}
              <span className="text-[13px] text-[#33443C]">
                {textoTransporteJornada(tJ)}
                {tJ && tJ.coste_total != null && Number(tJ.distancia_km) > 0 && (
                  <span className="block text-xs text-[#5B6B64]">Viaje, coste interno: {formatEuros(tJ.coste_total)}</span>
                )}
              </span>
            </div>
          );
        })}
        <p className="text-xs text-[#5B6B64]">
          Elige una jornada arriba (o pulsa su nombre) para ver y cambiar su horario, su lugar y quién va.
        </p>
      </div>
    );
  }
  return (
    <div className="overflow-x-auto rounded-xl border border-[#E1E6E3] bg-white" data-testid="l2-resumen-jornadas">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-[#F7F9F8] text-left">
            {['Jornada', 'Fecha', 'Horas', 'Lugar', 'Transporte'].map(t => (
              <th key={t} className="px-3 py-2 text-xs font-bold tracking-wide uppercase text-[#5B6B64] border-b border-[#E1E6E3]">{t}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {jornadas.map((j, i) => {
            const tJ = jornadasTransporte.find(x => x.id === j.id) || null;
            const hereda = jornadaHeredaDireccion(j, tJ);
            const linea = hereda
              ? lineaRaiz
              : (direccionCorta(j.direccion_exacta || j.lugar_evento).linea || j.lugar_evento || '');
            return (
              <tr key={j.id} className="border-b border-[#EEF1EF] last:border-b-0 align-top" data-testid={`l2-resumen-jornada-${j.id}`}>
                <td className="px-3 py-2.5">
                  <button type="button" onClick={() => onElegir && onElegir(j.id)} className={enlace} data-testid={`l2-resumen-abrir-${j.id}`}>
                    Jornada {i + 1}
                  </button>
                </td>
                <td className="px-3 py-2.5 whitespace-nowrap">
                  {fechaCorta(j.fecha) || 'Sin fecha'}
                  {diaSemana(j.fecha) && <span className="block text-xs text-[#5B6B64]">{diaSemana(j.fecha)}</span>}
                </td>
                <td className="px-3 py-2.5 whitespace-nowrap tabular-nums">{textoHorasJornada(j)}</td>
                <td className="px-3 py-2.5 min-w-[180px]">
                  {linea || <span className="text-[#92400E]">Falta la dirección</span>}
                  {hereda && linea && <span className="block text-xs text-[#5B6B64]">Hereda la dirección principal</span>}
                  {tJ?.madrid_central_dentro === true && (
                    <span className="mt-1 inline-flex items-center rounded-full border border-[#F2C4BF] bg-[#FDECEA] px-2 py-0.5 text-xs md:text-[11px] font-semibold text-[#8A1C12]">
                      Dentro de Madrid Central
                    </span>
                  )}
                </td>
                <td className="px-3 py-2.5 min-w-[180px] text-[#33443C]">
                  {textoTransporteJornada(tJ)}
                  {tJ && tJ.coste_total != null && Number(tJ.distancia_km) > 0 && (
                    <span className="block text-xs text-[#5B6B64]">Viaje, coste interno: {formatEuros(tJ.coste_total)}</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="px-3 py-2 text-xs text-[#5B6B64] border-t border-[#EEF1EF]">
        Elige una jornada arriba (o pulsa su nombre) para ver y cambiar su horario, su lugar y quién va.
      </p>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Nivel 1 con una jornada: lugar, ruta y transporte de la jornada
// ─────────────────────────────────────────────────────────────────────────────

export const LugarJornada = ({
  presupuesto, jornada, indice = 0, transporteJornada = null, reglasComerciales = null, modo = null,
  sinCoste = false, onGuardarJornada,
}) => {
  const p = presupuesto || {};
  const j = jornada || {};
  const tJ = transporteJornada;
  const hereda = jornadaHeredaDireccion(j, tJ);
  const [editando, setEditando] = useState(false);
  const [texto, setTexto] = useState(j.lugar_evento || '');
  const [acceso, setAcceso] = useState(j.detalles_acceso || '');
  const [ocupado, setOcupado] = useState(false);
  const [aviso, setAviso] = useState(null);

  useEffect(() => { setTexto(j.lugar_evento || ''); }, [j.id, j.lugar_evento]);
  useEffect(() => { setAcceso(j.detalles_acceso || ''); }, [j.id, j.detalles_acceso]);
  useEffect(() => { setEditando(false); setAviso(null); }, [j.id]);

  const ctx = presupuestoDeJornada(p, j, tJ, modo);
  const corta = direccionCorta(ctx.direccion_exacta || ctx.lugar_evento, tJ?.municipio);
  const mapa = urlMapa(ctx);
  const rutaBase = costeRutaInterno({
    presupuesto: ctx, reglas: reglasComerciales, transporte: tJ, sinCoste: !!sinCoste && modo === 'propio',
  });
  const faltaRuta = !hereda && !(Number(j.distancia_km) > 0);

  const guardar = async (cambios, textoOk) => {
    if (!onGuardarJornada) return false;
    setOcupado(true);
    setAviso(null);
    try {
      await onGuardarJornada(j.id, cambios);
      if (textoOk) setAviso({ tipo: 'ok', texto: textoOk });
      return true;
    } catch (err) {
      setAviso({ tipo: 'error', texto: `No se pudo guardar: ${err?.response?.data?.detail || err.message}` });
      return false;
    } finally {
      setOcupado(false);
    }
  };

  // Ruta desde la nave para esta jornada (coordenadas o, si no, el texto)
  const pedirRuta = async (lat, lng, direccion) => {
    try {
      const { data } = lat != null && lng != null
        ? await placesAPI.distancia(lat, lng, modo)
        : await placesAPI.distanciaDireccion(direccion, modo);
      if (data && data.disponible) {
        return {
          distancia_km: Number(Number(data.distancia_km).toFixed(2)),
          tiempo_viaje_min: parseInt(data.tiempo_min, 10) || 0,
          coste_transporte: Number(Number(data.coste_transporte || 0).toFixed(2)),
        };
      }
    } catch (err) {
      // Sin ruta: la dirección se guarda igual y queda pendiente
    }
    return null;
  };

  const seleccionar = async (lugar) => {
    const datos = {
      lugar_evento: lugar.nombre || lugar.direccion,
      direccion_exacta: lugar.direccion,
      place_id: lugar.place_id || null,
      ...(lugar.lat != null ? { coordenadas: { lat: lugar.lat, lng: lugar.lng } } : {}),
      ...(lugar.municipio ? { municipio: lugar.municipio } : {}),
    };
    setOcupado(true);
    const ruta = await pedirRuta(lugar.lat, lugar.lng, lugar.direccion);
    const borrar = [
      ...(lugar.lat != null ? [] : ['coordenadas']),
      ...(lugar.municipio ? [] : ['municipio']),
      ...(ruta ? [] : CAMPOS_RUTA),
    ];
    const ok = await guardar({ ...datos, ...(ruta || {}), ...(borrar.length ? { borrar } : {}) },
      ruta ? 'Lugar de la jornada guardado con su ruta.' : 'Lugar guardado. Falta la ruta: vuelve a calcularla.');
    if (ok) setEditando(false);
  };

  const recalcular = async () => {
    setOcupado(true);
    const c = j.coordenadas || {};
    const ruta = await pedirRuta(c.lat ?? null, c.lng ?? null, j.direccion_exacta || j.lugar_evento);
    if (!ruta) {
      setOcupado(false);
      setAviso({ tipo: 'error', texto: 'Google no encuentra la ruta con esta dirección: elige la sugerencia de Google.' });
      return;
    }
    await guardar(ruta, 'Ruta de la jornada calculada.');
  };

  const usarPrincipal = () => guardar({ borrar: CAMPOS_LUGAR_JORNADA }, 'La jornada vuelve a usar la dirección principal.');

  const guardarAcceso = () => {
    const limpio = acceso.trim();
    if (limpio === (j.detalles_acceso || '').trim()) return;
    guardar(limpio ? { detalles_acceso: limpio } : { borrar: ['detalles_acceso'] }, 'Detalles de acceso guardados.');
  };

  const verEditor = editando || (!hereda && (faltaRuta || !j.lugar_evento));

  return (
    <div className="flex flex-col gap-3" data-testid="l2-lugar-jornada">
      <p className="text-xs font-semibold text-[#5B6B64]">
        Jornada {indice + 1}{j.fecha ? ` · ${fechaCorta(j.fecha)}` : ''} · {textoHorasJornada(j)}
      </p>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-sm text-[#16221D]" data-testid="l2-lugar-jornada-texto">
          <strong>{corta.linea || ctx.lugar_evento || 'Sin dirección'}</strong>
          {hereda && (
            <span className="ml-2 inline-flex items-center rounded-full bg-[#EEF1EF] px-2 py-0.5 text-xs md:text-[11px] font-semibold text-[#4A5A53] align-middle"
              data-testid="l2-lugar-jornada-hereda">
              Hereda la dirección principal
            </span>
          )}
          {tJ?.madrid_central_dentro === true && (
            <span className="ml-2 inline-flex items-center rounded-full border border-[#F2C4BF] bg-[#FDECEA] px-2 py-0.5 text-xs md:text-[11px] font-semibold text-[#8A1C12] align-middle"
              data-testid="l2-lugar-jornada-madrid-central">
              Dentro de Madrid Central
            </span>
          )}
          {mapa && (
            <>
              {' '}
              <a href={mapa} target="_blank" rel="noopener noreferrer" className={`ml-1 ${enlacePequeno}`}>Ver en mapa</a>
            </>
          )}
        </p>
        <span className="flex flex-wrap items-center gap-3">
          {!verEditor && (
            <button type="button" onClick={() => setEditando(true)} className={enlacePequeno} data-testid="l2-lugar-jornada-cambiar">
              {hereda ? 'Poner otra dirección en esta jornada' : 'Cambiar'}
            </button>
          )}
          {!hereda && (
            <button type="button" onClick={usarPrincipal} disabled={ocupado} className={enlacePequeno} data-testid="l2-lugar-jornada-principal">
              Usar la dirección principal
            </button>
          )}
        </span>
      </div>

      {rutaBase && (
        <p className="text-sm text-[#5B6B64]" data-testid="l2-lugar-jornada-ruta">
          Ruta desde la nave: {textoRutaInterna(rutaBase, { corto: true })}
          {hereda && tJ?.hereda_ruta ? ` (${TEXTO_RUTA_HEREDADA.toLowerCase()})` : ''}
        </p>
      )}
      {rutaBase && <CosteViaje ruta={rutaBase} transporte={tJ ? { coste_combustible: tJ.coste_combustible } : null} />}

      {verEditor && (
        <div className="flex flex-col gap-2 rounded-lg border border-[#E1E6E3] px-3.5 py-3" data-testid="l2-lugar-jornada-editor">
          <label htmlFor={`l2-direccion-${j.id}`} className="text-xs font-semibold text-[#5B6B64]">Lugar o dirección de esta jornada</label>
          <DireccionAutocomplete
            id={`l2-direccion-${j.id}`}
            value={texto}
            onChange={setTexto}
            onSelect={seleccionar}
            disabled={ocupado}
            placeholder="Busca el lugar o la dirección..."
            className="w-full px-3 py-2 text-sm border border-[#CBD3CF] rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
          />
          {faltaRuta && !hereda && (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-[10px] border px-3.5 py-2.5 text-sm bg-[#FDF0DC] border-[#F4D9AE] text-[#7A3A06]">
              <span>{ocupado ? 'Calculando la ruta desde la nave...' : 'Falta la ruta desde la nave: calcúlala o elige la sugerencia de Google.'}</span>
              {!ocupado && (
                <button type="button" onClick={recalcular} className={botonSecundario} data-testid="l2-lugar-jornada-calcular">Calcular ruta</button>
              )}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-4">
            {!faltaRuta && !hereda && (
              <button type="button" onClick={recalcular} disabled={ocupado} className={enlacePequeno} data-testid="l2-lugar-jornada-recalcular">
                {ocupado ? 'Calculando...' : 'Volver a calcular la ruta'}
              </button>
            )}
            {editando && (
              <button type="button" onClick={() => setEditando(false)} className={enlace}>Listo</button>
            )}
          </div>
        </div>
      )}

      <div className="flex flex-col gap-1">
        <label htmlFor={`l2-acceso-${j.id}`} className="text-xs font-semibold text-[#5B6B64]">
          Detalles de acceso de esta jornada (planta, contacto, aparcamiento...)
        </label>
        <textarea
          id={`l2-acceso-${j.id}`}
          rows={2}
          value={acceso}
          onChange={(e) => setAcceso(e.target.value)}
          onBlur={guardarAcceso}
          disabled={ocupado}
          className="w-full px-2 py-1.5 text-sm border border-[#CBD3CF] rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
          data-testid="l2-lugar-jornada-acceso"
        />
      </div>

      <div className="border-t border-[#EEF1EF] pt-3 flex flex-col gap-1.5" data-testid="l2-transporte-jornada">
        <h3 className={claseSubtitulo}>Transporte de esta jornada</h3>
        <p className="text-sm text-[#16221D]" data-testid="l2-transporte-jornada-zona">{textoTransporteJornada(tJ)}</p>
        <p className="text-xs text-[#5B6B64]" data-testid="l2-transporte-jornada-mc">
          {tJ?.madrid_central_dentro === true && 'Dentro de Madrid Central: el suplemento lleva una unidad por cada jornada dentro de la zona.'}
          {tJ?.madrid_central_dentro === false && 'No entra en Madrid Central.'}
          {(tJ?.madrid_central_dentro === null || tJ?.madrid_central_dentro === undefined) && 'Madrid Central: sin coordenadas para comprobarlo (elige la sugerencia de Google).'}
        </p>
        <p className="text-xs text-[#5B6B64]">
          Lo que se cobra al cliente por transporte y Madrid Central es una sola línea para todo el evento: se cambia en la vista Todas.
        </p>
      </div>

      {aviso && (
        <p className={`text-xs ${aviso.tipo === 'error' ? 'text-[#8A1C12]' : 'text-[#0F5E43]'}`} data-testid="l2-lugar-jornada-mensaje">{aviso.texto}</p>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Nivel 2 con una jornada: fecha y horas de la jornada
// ─────────────────────────────────────────────────────────────────────────────

const CAMPOS_HORARIO = [
  { campo: 'fecha', etiqueta: 'Fecha', tipo: 'date', obligatorio: true },
  { campo: 'hora_llegada_estimada', etiqueta: 'Llegada al lugar', tipo: 'time', obligatorio: false },
  { campo: 'hora_inicio', etiqueta: 'Inicio del evento', tipo: 'time', obligatorio: true },
  { campo: 'hora_fin', etiqueta: 'Fin del evento', tipo: 'time', obligatorio: true },
];

export const HorarioJornadaCampos = ({ jornada, indice = 0, onGuardarJornada }) => {
  const j = jornada || {};
  const valoresDe = (x) => Object.fromEntries(CAMPOS_HORARIO.map(({ campo }) => [
    campo, campo === 'fecha' ? String(x[campo] || '').slice(0, 10) : (x[campo] || ''),
  ]));
  const [valores, setValores] = useState(() => valoresDe(j));
  const [ocupado, setOcupado] = useState(false);
  const [aviso, setAviso] = useState(null);
  const firma = JSON.stringify(valoresDe(j));
  useEffect(() => { setValores(JSON.parse(firma)); }, [firma]);

  const guardarCampo = async (campo, obligatorio) => {
    const nuevo = valores[campo] || '';
    const antes = campo === 'fecha' ? String(j[campo] || '').slice(0, 10) : (j[campo] || '');
    if (nuevo === antes) return;
    if (!nuevo && obligatorio) {
      setValores(prev => ({ ...prev, [campo]: antes }));
      setAviso({ tipo: 'error', texto: 'La fecha y las horas de inicio y fin de la jornada no pueden quedar vacías.' });
      return;
    }
    setOcupado(true);
    setAviso(null);
    try {
      const guardada = await onGuardarJornada(j.id, nuevo ? { [campo]: nuevo } : { borrar: [campo] });
      const desplazados = Number(guardada?.serviciosDesplazados) || 0;
      const base = desplazados > 0
        ? `Horario de la jornada guardado. ${desplazados === 1 ? '1 servicio se ha movido' : `${desplazados} servicios se han movido`} a la nueva hora.`
        : 'Horario de la jornada guardado.';
      setAviso(guardada?.avisoHoras
        ? { tipo: 'aviso', texto: `${base} ${guardada.avisoHoras}` }
        : { tipo: 'ok', texto: base });
    } catch (err) {
      setAviso({ tipo: 'error', texto: `No se pudo guardar: ${err?.response?.data?.detail || err.message}` });
    } finally {
      setOcupado(false);
    }
  };

  return (
    <div className="flex flex-col gap-2" data-testid="l2-horario-jornada">
      <p className="text-xs font-semibold text-[#5B6B64]">Jornada {indice + 1}</p>
      <div className="flex flex-wrap items-end gap-3">
        {CAMPOS_HORARIO.map(({ campo, etiqueta, tipo, obligatorio }) => (
          <label key={campo} className="flex flex-col gap-1 text-xs font-semibold text-[#5B6B64]">
            {etiqueta}
            <input
              type={tipo}
              value={valores[campo]}
              onChange={(e) => setValores(prev => ({ ...prev, [campo]: e.target.value }))}
              onBlur={() => guardarCampo(campo, obligatorio)}
              disabled={ocupado || !onGuardarJornada}
              className={claseCampo}
              data-testid={`l2-horario-jornada-${campo}`}
            />
          </label>
        ))}
      </div>
      <p className="text-xs text-[#5B6B64]">
        Se guarda al salir de cada campo. El horario del responsable de abajo se calcula con estas horas y la ruta de esta jornada.
      </p>
      {aviso && (
        <p
          className={`text-xs ${{ error: 'text-[#8A1C12]', aviso: 'text-[#92400E] font-semibold' }[aviso.tipo] || 'text-[#0F5E43]'}`}
          data-testid="l2-horario-jornada-mensaje"
        >
          {aviso.texto}
        </p>
      )}
    </div>
  );
};
